<template>
  <el-dialog
    :model-value="visible"
    :before-close="() => {
      emit('close');
    }"
    width="580"
  >
    <template #header>
      <div class="my-header">
        <h4 class="px-2 pb-3 text-xl text-[var(--color-text-primary)] font-semibold">
          调整服务费
        </h4>
      </div>
    </template>

    <div class="px-2 pb-3">
      <el-form
        ref="formRef"
        require-asterisk-position="right"
        :model="formValues"
        :rules="formRules"
      >
        <el-form-item label="订单编号:">
          <span>{{ orderNo }}</span>
        </el-form-item>

        <el-form-item prop="orderNo" label="收款人名称/身份证号/子订单编号" label-position="top">
          <el-select
            v-model="formValues.orderNo"
            filterable
            remote
            clearable
            placeholder="可输入收款人名称/身份证号/子订单编号进行查询"
            :remote-method="queryByString"
            @change="onSelectSubOrder"
          >
            <template #label>
              {{ currentSubOrder.personName }} / {{ currentSubOrder.personIdNumber }} / {{ currentSubOrder.orderDetailNo }}
            </template>
            <el-option
              v-for="(item, idx) in subOrders"
              :key="idx"
              :value="item.orderDetailNo"
            >
              <div v-html="getHighlightHtml(keyword, `${item.personName} / ${item.personIdNumber} / ${item.orderDetailNo}`)" />
            </el-option>
          </el-select>
        </el-form-item>

        <template v-if="formValues.orderNo">
          <el-form-item label="收款人:">
            <span>{{ currentSubOrder.personName }}</span>
          </el-form-item>

          <el-form-item label="初始企业服务费:">
            <span>{{ currentSubOrder.initialCompanyServiceAmount }}元</span>
          </el-form-item>

          <el-form-item prop="companyServiceAmount" label="调整后的企业服务费" label-position="top">
            <el-input
              v-model="formValues.companyServiceAmount"
              class="w-[156px]"
              placeholder="请输入"
              clearable
              @keyup.enter="onConfirm"
            />
            <span class="ml-2">元</span>
          </el-form-item>
        </template>
      </el-form>
    </div>

    <template #footer>
      <div class="flex justify-end">
        <el-button @click="emit('close')">
          取消
        </el-button>
        <el-button type="primary" :loading="submiting" @click="onConfirm">
          确定
        </el-button>
      </div>
    </template>
  </el-dialog>
</template>

<script setup lang="ts">
// personName   personIdNumber  orderDetailNo
import { adjustSubOrderFee, queryAllSettlementDetailList } from '@core/main/api';
import { ISettlementDetailListItem, ISettlementOrderDetail } from '@core/main/types';
import { computed, PropType, ref, watch, toRaw } from 'vue';
import { ElMessage, FormInstance, FormRules } from 'element-plus';
import { useRoute } from 'vue-router';
import { checkNumber, getHighlightHtml } from '@core/tools';

interface IFormValues {
  companyServiceAmount: number | undefined;
  orderNo: string | undefined;
}

let allSubOrders: ISettlementDetailListItem[] = [];

const emit = defineEmits(['close', 'success']);

const route = useRoute();

const props = defineProps({
  visible: {
    type: Boolean,
    default: false,
  },
  data: {
    type: Object as PropType<ISettlementOrderDetail>,
    default: () => ({}),
  },
  module: {
    type: String as PropType<'zhongbao' | 'cuohe'>,
    default: 'zhongbao',
  },
});

const error = ref('');
const submiting = ref(false);

const formRules = ref<FormRules<IFormValues>>({
  orderNo: [
    {
      required: true,
      message: '请搜索并选择子订单',
    },
  ],
  companyServiceAmount: [
    {
      required: true,
      message: '请输入调整后的企业服务费',
    },
    {
      validator: (rule: any, value: string, callback: any) => {
        if (checkNumber(String(value), '调整后的企业服务费', 2)) {
          callback(checkNumber(String(value), '调整后的企业服务费', 2));
        }
        callback();
      },
    },
  ],
});

const keyword = ref('');
const subOrders = ref<ISettlementDetailListItem[]>([]);
const currentSubOrder = ref({} as ISettlementDetailListItem);
const formRef = ref<FormInstance>();
const formValues = ref({
  companyServiceAmount: undefined,
  orderNo: undefined,
} as IFormValues);

const orderId = computed(() => Number(route.query.id));
const orderNo = computed(() => route.query.orderNo as string);

const onConfirm = () => {
  if (error.value) return;

  formRef.value?.validate(async (valid) => {
    if (valid) {
      doSubmit();
    }
  });
};

function onSelectSubOrder(orderDetailNo: string) {
  keyword.value = '';
  currentSubOrder.value = subOrders.value.find((item) => item.orderDetailNo === orderDetailNo) || {} as ISettlementDetailListItem;

  const resetAmount = Number(currentSubOrder.value.companyServiceAmount) === Number(currentSubOrder.value.initialCompanyServiceAmount);

  subOrders.value = toRaw(allSubOrders);
  formValues.value.companyServiceAmount = resetAmount ? undefined : currentSubOrder.value.companyServiceAmount;
}

async function queryByString(str: string) {
  if (!str.trim()) {
    keyword.value = '';
    return;
  }

  if (str) {
    const filteredItems = allSubOrders.filter((item) => {
      const matchName = item.personName.toLowerCase().includes(str.toLowerCase());
      const matchIdNumber = item.personIdNumber.toLowerCase().includes(str.toLowerCase());
      const matchOrderDetailNo = item.orderDetailNo.toLowerCase().includes(str.toLowerCase());

      return matchName || matchIdNumber || matchOrderDetailNo;
    });

    keyword.value = str;
    subOrders.value = filteredItems;
  } else {
    subOrders.value = [];
  }
}

async function doSubmit() {
  submiting.value = true;

  try {
    await adjustSubOrderFee(
      currentSubOrder.value.orderDetailId,
      Number(formValues.value.companyServiceAmount),
      props.module,
    );

    ElMessage.success('调整企业服务费成功');
    emit('close');

    setTimeout(() => {
      emit('success');
    }, 300);
  } catch (error) {
    ElMessage.error((error as Error).message || '调整企业服务费失败');
  }

  submiting.value = false;
}

async function fetchSubOrders() {
  try {
    allSubOrders = await queryAllSettlementDetailList(orderId.value, props.module);
  } catch (error) {
    if ((error as Error).message) ElMessage.error((error as Error).message || '查询子订单列表失败');
  }
}

watch(
  () => props.visible,
  (visible) => {
    if (visible) {
      fetchSubOrders();
    } else {
      subOrders.value = [];
      currentSubOrder.value = {} as ISettlementDetailListItem;
      formValues.value = {
        companyServiceAmount: undefined,
        orderNo: undefined,
      };

      formRef.value?.clearValidate();
    }
  },
  { immediate: true },
);
</script>
